import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Loader, Rocket, Sparkles, TriangleAlert } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { ServerRow } from '../../utils/statusMonitorTable';
import type { MaintenanceMode, MaintenanceOptions } from '../../services/maintenanceApi';
import { parseReleaseInput, releaseInputValue, sharedReleaseAt } from '../../utils/comingSoon';
import { WindowModeFields } from './ComingSoon';

export interface MaintenanceTarget {
  /** What the menu calls the whole group, e.g. the cluster id. */
  label: string;
  rows: ServerRow[];
}

interface MaintenanceControlProps {
  target: MaintenanceTarget;
  /** Resolves once the change has been applied and the flags re-read. */
  onToggle: (addresses: string[], maintenance: boolean, options?: MaintenanceOptions) => Promise<void>;
  className?: string;
}

/**
 * One control for both jobs: the whole cluster, or a single server inside it.
 *
 * A button per server would put four or five identical controls on every card
 * for something used a few times a month, so this is one button that opens the
 * choice. The whole-cluster row is first because that is the common case: a
 * pack goes down for an update, not one region of it.
 *
 * A window is one of two kinds, picked at the top of the menu: plain
 * maintenance, or "coming soon" for a new server shown to the public before it
 * opens, with an optional release time the public pages count down to.
 * Starting a window on servers already in one re-states it, which is how a
 * release time is moved or a launch turned into plain maintenance. Ending a
 * coming-soon window is called "Open to the public", because that is what it
 * does.
 *
 * The menu states what will happen rather than what is true now ("Start
 * maintenance", not "In maintenance"), because it is a list of actions.
 */
export function MaintenanceControl({ target, onToggle, className }: MaintenanceControlProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<MaintenanceMode>('maintenance');
  const [release, setRelease] = useState('');
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const container = useRef<HTMLDivElement>(null);

  const rows = target.rows.filter(row => row.address);
  const inWindow = rows.filter(row => row.maintenance);
  const comingSoon = rows.filter(row => row.comingSoon);
  const all = rows.length > 0 && inWindow.length === rows.length;
  const some = inWindow.length > 0;
  const allSoon = rows.length > 0 && comingSoon.length === rows.length;
  // The whole window is a launch when every server in it is coming soon.
  const windowIsLaunch = some && comingSoon.length === inWindow.length;

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('mousedown', dismiss); document.removeEventListener('keydown', escape); };
  }, [open]);

  // Opening the menu starts from what is there: a card that is all coming
  // soon opens on that kind, with its shared release time filled in, so
  // moving the date is one edit rather than retyping the launch.
  const toggleOpen = () => {
    if (!open) {
      const shared = allSoon ? sharedReleaseAt(comingSoon) : undefined;
      setMode(allSoon ? 'coming_soon' : 'maintenance');
      setRelease(allSoon && typeof shared === 'number' ? releaseInputValue(shared) : '');
      setReleaseError(null);
      setError(null);
    }
    setOpen(value => !value);
  };

  const run = async (key: string, addresses: string[], maintenance: boolean) => {
    if (busy) return;
    let options: MaintenanceOptions | undefined;
    if (maintenance) {
      options = { mode };
      if (mode === 'coming_soon') {
        const parsed = parseReleaseInput(release, Date.now());
        if (!parsed.ok) {
          setReleaseError(parsed.error);
          return;
        }
        options.releaseAt = parsed.releaseAt;
      }
    }
    setBusy(key);
    setError(null);
    setReleaseError(null);
    try {
      await onToggle(addresses, maintenance, options);
      setOpen(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That change could not be applied.');
    } finally {
      setBusy(null);
    }
  };

  if (rows.length === 0) return null;

  const startLabel = mode === 'coming_soon'
    ? (allSoon ? 'Update coming soon' : 'Mark coming soon')
    : (comingSoon.length > 0 ? 'Switch to maintenance' : 'Start maintenance');
  const endLabel = windowIsLaunch ? 'Open to the public' : 'End maintenance';
  const buttonLabel = allSoon ? 'Coming soon'
    : all ? 'In maintenance'
      : comingSoon.length > 0 && comingSoon.length === inWindow.length ? `${comingSoon.length} coming soon`
        : some ? `${inWindow.length} in a window` : 'Maintenance';

  return (
    <div className={cn('monitor-maintenance-control', className)} ref={container}>
      <button
        type="button"
        className={cn('monitor-maintenance-button', some && (windowIsLaunch ? 'monitor-maintenance-button-soon' : 'monitor-maintenance-button-active'))}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={toggleOpen}
        title={all ? `${target.label} is ${allSoon ? 'coming soon' : 'in maintenance'}` : some ? `${inWindow.length} of ${rows.length} in a window` : `Maintenance for ${target.label}`}
      >
        {windowIsLaunch ? <Sparkles className="w-3.5 h-3.5" aria-hidden /> : <TriangleAlert className="w-3.5 h-3.5" aria-hidden />}
        <span>{buttonLabel}</span>
        <ChevronDown className={cn('w-3 h-3 transition-transform', open && 'rotate-180')} aria-hidden />
      </button>

      {open && (
        <div className="monitor-maintenance-menu monitor-maintenance-menu-wide" role="menu" aria-label={`Maintenance for ${target.label}`}>
          <WindowModeFields
            className="monitor-maintenance-mode"
            mode={mode}
            onMode={(next) => { setMode(next); setReleaseError(null); }}
            release={release}
            onRelease={(value) => { setRelease(value); setReleaseError(null); }}
            error={releaseError}
            disabled={busy !== null}
          />
          <div className="monitor-maintenance-divider" role="separator" />

          <button
            type="button"
            role="menuitem"
            className="monitor-maintenance-item monitor-maintenance-item-all"
            disabled={busy !== null}
            onClick={() => run('start', rows.map(row => row.address), true)}
          >
            {busy === 'start'
              ? <Loader className="w-3.5 h-3.5 animate-spin" aria-hidden />
              : <span className={cn('monitor-maintenance-swatch', mode === 'coming_soon' && 'monitor-maintenance-swatch-soon')} aria-hidden />}
            <span className="flex-1 text-left">
              {startLabel}
              <span className="monitor-maintenance-scope">{rows.length === 1 ? target.label : `all of ${target.label}`}</span>
            </span>
          </button>

          {some && (
            <button
              type="button"
              role="menuitem"
              className="monitor-maintenance-item monitor-maintenance-item-all"
              disabled={busy !== null}
              onClick={() => run('end', inWindow.map(row => row.address), false)}
            >
              {busy === 'end'
                ? <Loader className="w-3.5 h-3.5 animate-spin" aria-hidden />
                : windowIsLaunch ? <Rocket className="w-3.5 h-3.5 monitor-tone-soon" aria-hidden /> : <Check className="w-3.5 h-3.5 monitor-tone-good" aria-hidden />}
              <span className="flex-1 text-left">
                {endLabel}
                <span className="monitor-maintenance-scope">
                  {inWindow.length === rows.length
                    ? (rows.length === 1 ? target.label : `all of ${target.label}`)
                    : `${inWindow.length} of ${rows.length} servers`}
                </span>
              </span>
            </button>
          )}

          {rows.length > 1 && (
            <>
              <div className="monitor-maintenance-divider" role="separator" />
              {rows.map(row => (
                <button
                  key={row.id}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={row.maintenance}
                  className="monitor-maintenance-item"
                  disabled={busy !== null}
                  title={row.maintenance
                    ? (row.comingSoon ? `Open ${row.address} to the public` : `End maintenance on ${row.address}`)
                    : (mode === 'coming_soon' ? `Mark ${row.address} coming soon` : `Start maintenance on ${row.address}`)}
                  onClick={() => run(row.id, [row.address], !row.maintenance)}
                >
                  {busy === row.id
                    ? <Loader className="w-3.5 h-3.5 animate-spin" aria-hidden />
                    : row.comingSoon
                      ? <Sparkles className="w-3.5 h-3.5 monitor-tone-soon" aria-hidden />
                      : row.maintenance
                        ? <Check className="w-3.5 h-3.5 monitor-tone-warning" aria-hidden />
                        : <span className="monitor-maintenance-tick" aria-hidden />}
                  <span className="flex-1 text-left">
                    {row.stats.serverId || row.stats.identifier}
                    <span className="monitor-maintenance-scope">
                      {row.address}{row.comingSoon ? ' · coming soon' : row.maintenance ? ' · in maintenance' : ''}
                    </span>
                  </span>
                </button>
              ))}
            </>
          )}

          {error && <p className="monitor-maintenance-error" role="alert">{error}</p>}
        </div>
      )}
    </div>
  );
}
